import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import NavigatedViewer from "bpmn-js/lib/NavigatedViewer";
import "bpmn-js/dist/assets/diagram-js.css";
import "bpmn-js/dist/assets/bpmn-font/css/bpmn-embedded.css";
import { getDiagramAvailability } from "../../actions";
import { useViewConfig } from "../app/ViewConfigContext";
import { diagramProperties, elementProperties } from "./elementProperties";
import {
  accentBar,
  accentFor,
  describeElement,
  guidFromElementId,
  headerBand,
  indexObjects,
  isBox,
  isDrillableType,
  shapeFamily,
} from "./flowStyle";

export { guidFromElementId };

const SVG_NS = "http://www.w3.org/2000/svg";

// How the drawing itself is coloured and lettered. These go to bpmn-js's own
// renderer rather than into the stylesheet, because the renderer writes them
// into the SVG it draws - so a diagram saved or exported from the viewer
// carries the same typeface and the same calm strokes, where a CSS rule would
// have stayed behind in the browser.
const RENDERER = {
  defaultFillColor: "#ffffff",
  // A calm ink-grey rather than the library's black: it also colours the
  // arrowheads, which are drawn as markers and can't be reached from CSS.
  defaultStrokeColor: "#5b6278",
  defaultLabelColor: "#1a1a2e",
};

const FONT = '"Geist", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
// Sizes stay at bpmn-js's own (12 inside a shape, 11 for a flow's label): the
// label boxes in a stored diagram were measured for those, and a larger letter
// is what clips a caption.
const TEXT = {
  defaultStyle: { fontFamily: FONT, fontSize: 12, fontWeight: 500, lineHeight: 1.2 },
  externalStyle: { fontFamily: FONT, fontSize: 11, fontWeight: 500, lineHeight: 1.2 },
};

// Always show the whole diagram. Zooming to fill the width reads larger but
// crops what sits outside it, and a diagram that is cut off is worse than one
// with space around it; the reader zooms in themselves.
function fitSensibly(viewer) {
  viewer.get("canvas").zoom("fit-viewport", "auto");
}

// On request: fill the width and start at the top, reading down as you would
// through a document. Nothing is cut off sideways; the rest is a scroll away.
const EDGE_PADDING = 24;

function fitWidth(viewer) {
  const canvas = viewer.get("canvas");
  canvas.zoom("fit-viewport", "auto");
  const { inner, outer } = canvas.viewbox();
  if (!inner?.width || !outer?.width) return;
  const scale = Math.min((outer.width - EDGE_PADDING * 2) / inner.width, 4);
  canvas.zoom(scale, {
    x: inner.x + inner.width / 2,
    y: inner.y + outer.height / scale / 2 - EDGE_PADDING / scale,
  });
}

// One `<rect>` added to a shape's own drawing - the accent bar on a box, the
// title band on a pool. Marked `data-bf`, so it is told apart from the shape's
// own rectangle (the stylesheet never paints it) and adding it twice is a
// no-op when the decoration runs again.
function paint(gfx, box, mark, { fill, before }) {
  const visual = gfx?.querySelector(".djs-visual");
  if (!visual || !box) return;
  if (visual.querySelector(`[data-bf="${mark}"]`)) return;
  const rect = document.createElementNS(SVG_NS, "rect");
  rect.setAttribute("data-bf", mark);
  Object.entries(box).forEach(([name, value]) => rect.setAttribute(name, String(value)));
  rect.setAttribute("fill", fill);
  rect.setAttribute("stroke", "none");
  rect.setAttribute("pointer-events", "none");
  // A band belongs under the shape's border and its title, an accent bar on
  // top of the shape's own fill.
  if (before) visual.insertBefore(rect, visual.childNodes[1] || null);
  else visual.appendChild(rect);
}

// The browser's own tooltip, in the one way SVG offers it.
function describe(gfx, text) {
  if (!gfx || !text) return;
  let title = gfx.querySelector(":scope > title");
  if (!title) {
    title = document.createElementNS(SVG_NS, "title");
    gfx.insertBefore(title, gfx.firstChild);
  }
  if (title.textContent !== text) title.textContent = text;
}

// What the file itself says about one element, read off the model bpmn-js has
// already parsed. Nothing is fetched and nothing is stored: the reader clicked
// a shape in a drawing that is in the browser, and this is what that drawing
// says about it. `isDrawn` is the canvas's own question - whether the reader
// could have clicked that element themselves - which is what decides whether
// something it stands for (a pool's process) is folded into the answer.
function readProperties(viewer, el, read) {
  try {
    const registry = viewer.get("elementRegistry");
    const root = viewer.get("canvas").getRootElement();
    const canvasObjects = registry
      .getAll()
      .filter((other) => other !== root && !other.labelTarget)
      .map((other) => other.businessObject)
      .filter(Boolean);
    const ancestors = [];
    for (let up = el?.parent; up && up !== root; up = up.parent) {
      if (up.businessObject) ancestors.push(up.businessObject);
    }
    const isDrawn = (object) => Boolean(object && object.id && registry.get(object.id));
    return read(el === root ? root.businessObject : el?.businessObject, { canvasObjects, ancestors, isDrawn });
  } catch (err) {
    // A panel with nothing in it is a bad afternoon; a viewer that throws while
    // somebody clicks a box is a worse one.
    console.error("Couldn't read this element's own properties:", err);
    return null;
  }
}

// Everything the drawing contains, read the same way one clicked element is:
// its kind in words and every property it actually carries. That is what
// "Ask this diagram" searches, so the panel and the details panel can never
// disagree about what an element says.
function readElements(viewer) {
  try {
    const registry = viewer.get("elementRegistry");
    const root = viewer.get("canvas").getRootElement();
    const drawn = registry.getAll().filter((el) => el && el !== root && !el.labelTarget && el.businessObject);
    const canvasObjects = drawn.map((el) => el.businessObject);
    const isDrawn = (object) => Boolean(object && object.id && registry.get(object.id));
    return drawn.map((el) => {
      const ancestors = [];
      for (let up = el.parent; up && up !== root; up = up.parent) {
        if (up.businessObject) ancestors.push(up.businessObject);
      }
      const own = elementProperties(el.businessObject, { canvasObjects, ancestors, isDrawn });
      return { id: el.id, name: own.name, kind: own.kind, rows: own.rows };
    });
  } catch (err) {
    // The panel is worth less than the drawing: without this it offers
    // nothing, which is exactly what it should do when it can't read.
    console.error("Couldn't read what this diagram contains:", err);
    return [];
  }
}

// Lighting elements up is only ever a class on the element the id names, so
// nothing can be highlighted that the drawing doesn't contain. Everything
// else dims in the stylesheet; a connector dims less, because the skeleton of
// a process is what makes one lit step readable as part of it.
const ASKING = "bf-asking";
const ASK_HIT = "bf-ask-hit";
const ASK_LINK = "bf-ask-link";
const ASK_PEEK = "bf-ask-peek";
const ASK_INSIDE = "bf-ask-inside";

// ── The camera ───────────────────────────────────────────────────────────
//
// An answer the reader has to go looking for is not an answer. When a
// question picks elements out, the view moves to them - close enough to read,
// never so close that the step loses the process around it - and comes back
// to where the reader was when the question is cleared. The moment they pan
// or zoom themselves they are in charge again, and nothing moves until they
// ask another question.

// The one move: an answer arrives shape by shape, in the order the drawing
// lists them, and settles inside half a second however many there are.
const ARRIVAL_STEP_MS = 18;
const ARRIVAL_CAP_MS = 420;

const FRAME_PADDING = 0.18;
// Below this the labels on a matched step stop being readable, and fitting
// everything in is worth less than framing most of it. A model where even the
// densest handful won't fit is framed as best it can be and says how many are
// outside the view.
const MIN_READABLE_ZOOM = 0.45;
// Never closer than this: one matched step filling the screen tells a reader
// less than the same step with its neighbours either side of it.
const MAX_FRAME_ZOOM = 1.5;
const FRAME_MS = 320;
// How far inside the view an element has to be before stepping to it is worth
// moving the canvas at all. A camera that twitches is worse than one that
// waits.
const COMFORT = 0.12;

const reducedMotion = () => {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch (err) {
    return false;
  }
};

const boundsOf = (elements) => {
  let box = null;
  elements.forEach((el) => {
    const points = el.waypoints && el.waypoints.length ? el.waypoints : [{ x: el.x, y: el.y }, { x: el.x + (el.width || 0), y: el.y + (el.height || 0) }];
    points.forEach((point) => {
      if (!Number.isFinite(point?.x) || !Number.isFinite(point?.y)) return;
      box = box
        ? {
            minX: Math.min(box.minX, point.x),
            minY: Math.min(box.minY, point.y),
            maxX: Math.max(box.maxX, point.x),
            maxY: Math.max(box.maxY, point.y),
          }
        : { minX: point.x, minY: point.y, maxX: point.x, maxY: point.y };
    });
  });
  return box;
};

// A shape that holds other shapes is context, not a target: a lane is as wide
// as the model, and fitting to one fits the page. Structural - whether the
// drawing puts anything inside it - so no kind of container is named here.
const holdsOthers = (el) => Array.isArray(el?.children) && el.children.length > 0;

const insideOf = (el, out = []) => {
  (el?.children || []).forEach((child) => {
    if (child.labelTarget) return;
    if (holdsOthers(child)) insideOf(child, out);
    else out.push(child);
  });
  return out;
};

// What the camera should actually frame. The things inside a matched
// container beat the container; a container with nothing in it is all there
// is to show.
const framable = (found) => {
  const targets = found.filter((el) => !holdsOthers(el));
  if (targets.length) return targets;
  const contained = found.flatMap((el) => insideOf(el));
  return contained.length ? contained : found;
};

// Where most of the matches are, when they don't all fit at a size anyone can
// read. The window is what the view holds at that size; the answer is the
// fullest one, so the reader lands where there is most to see and is told
// what is outside it.
const densestWindow = (targets, width, height) => {
  const centre = (el) => {
    const box = boundsOf([el]);
    return box ? { x: (box.minX + box.maxX) / 2, y: (box.minY + box.maxY) / 2 } : null;
  };
  const points = targets.map((el) => ({ el, at: centre(el) })).filter((row) => row.at);
  let best = null;
  points.forEach((row) => {
    const near = points.filter(
      (other) => Math.abs(other.at.x - row.at.x) <= width / 2 && Math.abs(other.at.y - row.at.y) <= height / 2
    );
    if (!best || near.length > best.length) best = near.map((other) => other.el);
  });
  return best || targets;
};

const easeOut = (t) => 1 - (1 - t) ** 3;

// One move of the camera, in one pass. diagram-js sets a viewbox instantly,
// so the easing is ours; under `prefers-reduced-motion` there is none.
const moveView = (canvas, to, animate, handle) => {
  const from = canvas.viewbox();
  if (!animate || reducedMotion()) {
    canvas.viewbox(to);
    return;
  }
  const started = Date.now();
  const step = () => {
    const share = Math.min(1, (Date.now() - started) / FRAME_MS);
    const eased = easeOut(share);
    canvas.viewbox({
      x: from.x + (to.x - from.x) * eased,
      y: from.y + (to.y - from.y) * eased,
      width: from.width + (to.width - from.width) * eased,
      height: from.height + (to.height - from.height) * eased,
    });
    if (share < 1) handle.current = requestAnimationFrame(step);
  };
  cancelAnimationFrame(handle.current);
  handle.current = requestAnimationFrame(step);
};

const eachGraphic = (viewer, fn) => {
  let registry;
  let root;
  try {
    registry = viewer.get("elementRegistry");
    root = viewer.get("canvas").getRootElement();
  } catch (err) {
    return;
  }
  const graphicsOf = (el) => {
    try {
      return registry.getGraphics(el);
    } catch (err) {
      return null;
    }
  };
  registry.getAll().forEach((el) => {
    if (!el || el === root) return;
    const gfx = graphicsOf(el);
    if (gfx) fn(el, gfx, graphicsOf);
  });
};

// Interactive BPMN diagram. Elements that have their own diagram get a badge
// and open it on double-click (or from the badge); a single click selects the
// element and the details panel beside the canvas says what it is.
const BpmnCanvas = forwardRef(function BpmnCanvas(
  { xml, objects, onSelect, onDrill, onError, onDrillableCount, onDiagramProperties, onElements, onHoverElement },
  ref
) {
  const config = useViewConfig();
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const callbacks = useRef({ onSelect, onDrill, onError, onDrillableCount, onDiagramProperties, onElements, onHoverElement });
  callbacks.current = { onSelect, onDrill, onError, onDrillableCount, onDiagramProperties, onElements, onHoverElement };
  // What the decoration needs, read at the moment it runs rather than closed
  // over: the configuration refreshes on its own, and a diagram must not be
  // imported again for a colour.
  const dressing = useRef({ objects, config });
  dressing.current = { objects, config };
  const drillable = useRef(new Set());
  // Set once the viewer is up: says what an element is, for whoever selected
  // it - a click, or a result chosen in Ask this diagram.
  const reportRef = useRef(null);
  // The camera's own memory: where the reader was before a question moved the
  // view, whether they have taken charge since, and the frame in flight.
  const savedView = useRef(null);
  const readerMoved = useRef(false);
  const framing = useRef(0);
  // Bumped once a diagram is on the canvas, and again once its badges are
  // known: the decoration runs after each, and whenever the objects or the
  // configuration change.
  const [imported, setImported] = useState(0);
  const [badges, setBadges] = useState(0);

  useImperativeHandle(ref, () => ({
    zoomIn: () => viewerRef.current?.get("zoomScroll").stepZoom(1),
    zoomOut: () => viewerRef.current?.get("zoomScroll").stepZoom(-1),
    fit: () => viewerRef.current && fitSensibly(viewerRef.current),
    fitWidth: () => viewerRef.current && fitWidth(viewerRef.current),
    // What an answer looks like on the drawing. Strictly by id: an id that
    // isn't in the registry lights nothing, and no answer can invent a step.
    highlight: (ids) => {
      const viewer = viewerRef.current;
      const container = containerRef.current;
      if (!viewer || !container) return;
      const wanted = new Set((ids || []).map(String));
      const asking = wanted.size > 0;
      const wasAsking = container.classList.contains(ASKING);
      container.classList.toggle(ASKING, asking);
      // The answer arrives in the order the drawing is written in, one shape
      // after the next, rather than all at once - so a reader sees the answer
      // sweep through the process instead of the page blinking. One move,
      // capped, and none at all where motion isn't wanted.
      const arrival = new Map();
      (ids || []).forEach((id, at) => arrival.set(String(id), Math.min(at * ARRIVAL_STEP_MS, ARRIVAL_CAP_MS)));
      eachGraphic(viewer, (el, gfx, graphicsOf) => {
        const target = el.labelTarget ? el.labelTarget.id : el.id;
        // A connector between two matched elements is part of the answer: a
        // matched path should read as a path, not as scattered boxes.
        const joinsTwo =
          el.waypoints && el.source && el.target && wanted.has(el.source.id) && wanted.has(el.target.id);
        gfx.classList.toggle(ASK_HIT, wanted.has(target) || Boolean(joinsTwo));
        gfx.style.setProperty("--bf-ask-delay", `${arrival.get(target) || 0}ms`);
        // What sits inside something that matched is why a reader asked for
        // it: a lane whose steps had faded out would be an outline round an
        // empty band. Its contents stay readable without being claimed as
        // matches - only an id that was answered with is ever washed.
        const held = holdsOthers(el) && wanted.has(el.id);
        gfx.classList.toggle(ASK_INSIDE, false);
        if (held) insideOf(el).forEach((child) => {
          const childGfx = graphicsOf(child);
          if (childGfx && !wanted.has(child.id)) childGfx.classList.add(ASK_INSIDE);
        });
        if (el.waypoints) gfx.classList.add(ASK_LINK);
      });
      if (asking && !wasAsking) {
        // Where the reader was before the first question, to come back to.
        try {
          savedView.current = viewer.get("canvas").viewbox();
        } catch (err) {
          savedView.current = null;
        }
      }
      if (asking) readerMoved.current = false;
      if (!asking && wasAsking && savedView.current) {
        try {
          const { x, y, width, height } = savedView.current;
          moveView(viewer.get("canvas"), { x, y, width, height }, true, framing);
        } catch (err) {
          // The drawing is still there; only the camera stayed put.
        }
        savedView.current = null;
      }
    },
    // The answer, framed: the whole of what matched, with room around it.
    // Never closer than a comfortable reading size, never further out than
    // the whole drawing, and never at all once the reader has taken the
    // camera themselves - unless they ask for it again.
    frame: (ids, { force = false } = {}) => {
      const viewer = viewerRef.current;
      const nothing = { framed: 0, outside: 0 };
      if (!viewer || !(ids || []).length) return nothing;
      if (readerMoved.current && !force) return nothing;
      try {
        const registry = viewer.get("elementRegistry");
        const canvas = viewer.get("canvas");
        const found = ids.map((id) => registry.get(String(id))).filter(Boolean);
        const targets = framable(found);
        const view = canvas.viewbox();
        const outer = view.outer;
        if (!outer?.width || !outer?.height) return nothing;

        const fitFor = (list) => {
          const box = boundsOf(list);
          if (!box) return null;
          const width = Math.max(box.maxX - box.minX, 1);
          const height = Math.max(box.maxY - box.minY, 1);
          const pad = Math.max(width, height) * FRAME_PADDING + 40;
          const scale = Math.min(outer.width / (width + pad * 2), outer.height / (height + pad * 2));
          return { box, scale };
        };

        let fit = fitFor(targets);
        if (!fit) return nothing;
        let shown = targets;
        // Everything at a size nobody can read is worse than most of it at a
        // size they can: frame where the matches are thickest instead, and
        // let the reader step to the rest.
        if (fit.scale < MIN_READABLE_ZOOM && targets.length > 1) {
          const cluster = densestWindow(targets, outer.width / MIN_READABLE_ZOOM, outer.height / MIN_READABLE_ZOOM);
          const clustered = fitFor(cluster);
          if (clustered && cluster.length) {
            fit = clustered;
            shown = cluster;
          }
        }
        const scale = Math.min(fit.scale, MAX_FRAME_ZOOM);
        const viewWidth = outer.width / scale;
        const viewHeight = outer.height / scale;
        const to = {
          x: (fit.box.minX + fit.box.maxX) / 2 - viewWidth / 2,
          y: (fit.box.minY + fit.box.maxY) / 2 - viewHeight / 2,
          width: viewWidth,
          height: viewHeight,
        };
        moveView(canvas, to, true, framing);
        readerMoved.current = false;
        const outside = targets.filter((el) => {
          const box = boundsOf([el]);
          return (
            box &&
            (box.maxX < to.x || box.minX > to.x + to.width || box.maxY < to.y || box.minY > to.y + to.height)
          );
        }).length;
        return { framed: shown.length, outside };
      } catch (err) {
        // Framing is a courtesy; the highlight already says where to look.
        return nothing;
      }
    },
    // A soft emphasis under the pointer: the element the reader is about to
    // choose, drawn in its own colour, with nothing moved.
    preview: (id) => {
      const viewer = viewerRef.current;
      if (!viewer) return;
      eachGraphic(viewer, (el, gfx) => {
        const target = el.labelTarget ? el.labelTarget.id : el.id;
        gfx.classList.toggle(ASK_PEEK, Boolean(id) && target === String(id));
      });
    },
    // One result, chosen: selected on the canvas and brought into view. The
    // selection is the canvas's own, so it wears the ring it always does.
    reveal: (id, { select = true } = {}) => {
      const viewer = viewerRef.current;
      if (!viewer || !id) return;
      try {
        const canvas = viewer.get("canvas");
        const el = viewer.get("elementRegistry").get(String(id));
        if (!el) return;
        if (select) {
          viewer.get("selection").select(el);
          // Selecting it on the canvas is only half of what a click does: the
          // panel beside the drawing has to follow too, or it keeps showing
          // whatever was clicked last while the canvas shows something else
          // (product owner's reviewer, 28 Sep 2026: "if I select one task from
          // the list the right side attribute panel should change as well").
          reportRef.current?.(el);
        }
        const box = boundsOf([el]);
        const view = canvas.viewbox();
        if (!box || !view?.width) return;
        // Only where it isn't comfortably on screen already: a step the
        // reader can see should never make the canvas move.
        const inset = { x: view.width * COMFORT, y: view.height * COMFORT };
        const inside =
          box.minX >= view.x + inset.x &&
          box.maxX <= view.x + view.width - inset.x &&
          box.minY >= view.y + inset.y &&
          box.maxY <= view.y + view.height - inset.y;
        if (inside) return;
        moveView(
          canvas,
          {
            x: (box.minX + box.maxX) / 2 - view.width / 2,
            y: (box.minY + box.maxY) / 2 - view.height / 2,
            width: view.width,
            height: view.height,
          },
          true,
          framing
        );
      } catch (err) {
        // Older diagram-js, or an element that went with a re-import: the
        // highlight still says where it is.
      }
    },
  }));

  useEffect(() => {
    const viewer = new NavigatedViewer({
      container: containerRef.current,
      bpmnRenderer: RENDERER,
      textRenderer: TEXT,
      // Arrow keys pan the diagram, but only while the canvas itself has
      // focus - never while the reader is typing somewhere else on the page.
      keyboard: { bindTo: containerRef.current },
    });
    viewerRef.current = viewer;
    const eventBus = viewer.get("eventBus");

    // What a selected element is, handed up for the panel beside the drawing.
    // A click and a step chosen in Ask this diagram both arrive here, so the
    // panel says the same thing however the reader got to the element.
    const report = (element) => {
      // A caption drawn outside its shape is its own element on the canvas.
      // Choosing one means the shape - not "nothing".
      const el = element?.labelTarget || element;
      if (!el || el.type === "bpmn:Process" || el.type === "bpmn:Collaboration") {
        callbacks.current.onSelect?.(null);
        return;
      }
      const own = readProperties(viewer, el, elementProperties);
      callbacks.current.onSelect?.({
        guid: guidFromElementId(el.id),
        name: own?.name || el.businessObject?.name || "",
        bpmnType: el.type,
        hasDiagram: drillable.current.has(el.id),
        // What the file says about it: its kind in words, and every property
        // it actually carries. The panel needs nothing else to fill itself.
        kind: own?.kind || "",
        properties: own?.rows || [],
      });
    };
    reportRef.current = report;

    eventBus.on("element.click", (e) => report(e.element));

    // Pointing at a shape says so, so a list beside the drawing can point at
    // the same thing the reader is.
    eventBus.on("element.hover", (e) => {
      const el = e.element?.labelTarget || e.element;
      callbacks.current.onHoverElement?.(el?.id || null);
    });
    eventBus.on("element.out", () => callbacks.current.onHoverElement?.(null));

    eventBus.on("element.dblclick", 1500, (e) => {
      const el = e.element?.labelTarget || e.element;
      if (drillable.current.has(el?.id)) {
        callbacks.current.onDrill?.(guidFromElementId(el.id));
        return false;
      }
      return undefined;
    });

    // The reader is always in charge: the moment they pan or zoom themselves,
    // no question moves the camera again until they ask another one.
    const container = containerRef.current;
    const frames = framing;
    const took = () => {
      readerMoved.current = true;
    };
    container?.addEventListener("wheel", took, { passive: true });
    container?.addEventListener("mousedown", took);
    container?.addEventListener("touchstart", took, { passive: true });

    return () => {
      container?.removeEventListener("wheel", took);
      container?.removeEventListener("mousedown", took);
      container?.removeEventListener("touchstart", took);
      cancelAnimationFrame(frames.current);
      viewerRef.current = null;
      viewer.destroy();
    };
  }, []);

  // Which elements carry a diagram of their own. One request for the whole
  // diagram, and the badge is only ever offered where one actually exists.
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || !imported) return undefined;
    let cancelled = false;
    (async () => {
      const registry = viewer.get("elementRegistry");
      const overlays = viewer.get("overlays");
      const candidates = registry.filter((el) => isDrillableType(el.type));
      const availability = await getDiagramAvailability(candidates.map((el) => guidFromElementId(el.id)));
      if (cancelled || viewerRef.current !== viewer) return;
      candidates.forEach((el) => {
        const guid = guidFromElementId(el.id);
        if (!availability[guid] || drillable.current.has(el.id)) return;
        const label = el.businessObject?.name
          ? `Open the diagram for ${el.businessObject.name}`
          : "Open this step's diagram";
        const badge = document.createElement("button");
        badge.type = "button";
        badge.className = "bf-drill-badge";
        badge.title = "Open this step's diagram";
        // The shape itself is the control a screen reader and the keyboard
        // reach (below); the badge is the same action drawn where a mouse
        // expects it, so it stays out of the tab order rather than becoming a
        // second stop that does the same thing.
        badge.tabIndex = -1;
        badge.setAttribute("aria-hidden", "true");
        badge.addEventListener("click", (event) => {
          event.stopPropagation();
          callbacks.current.onDrill?.(guid);
        });
        // A reader who double-clicks the badge means it once, not twice.
        badge.addEventListener("dblclick", (event) => event.stopPropagation());
        try {
          overlays.add(el.id, "drill", { position: { top: -10, right: 10 }, html: badge });
        } catch (err) {
          // The element went with a diagram that has since been replaced.
          return;
        }
        drillable.current.add(el.id);
        const gfx = registry.getGraphics(el);
        if (gfx) {
          gfx.classList.add("bf-drillable");
          // The step is the control: reachable by Tab, announced as a button
          // with the diagram it opens, and opened with Enter or Space. The
          // mouse has the double-click and the badge; this is the same door.
          gfx.setAttribute("tabindex", "0");
          gfx.setAttribute("role", "button");
          gfx.setAttribute("aria-label", label);
          gfx.addEventListener("keydown", (event) => {
            if (event.key !== "Enter" && event.key !== " " && event.key !== "Spacebar") return;
            event.preventDefault();
            event.stopPropagation();
            callbacks.current.onDrill?.(guid);
          });
        }
      });
      callbacks.current.onDrillableCount?.(drillable.current.size);
      // The badges change what an element is: decorate again, so a step with
      // its own diagram is coloured and says so in its tooltip.
      if (!cancelled) setBadges((n) => n + 1);
    })().catch((err) => {
      // A diagram on the canvas is worth more than its badges.
      console.error("Couldn't check which elements have their own diagram:", err);
    });
    return () => {
      cancelled = true;
    };
  }, [imported]);

  // The product's own clothes on the drawing: one pass over the elements,
  // setting a family class, the type's colour where we know the object, the
  // tooltip and the two painted marks. Idempotent, so a configuration that
  // arrives late only re-reads what is already on the canvas - the diagram is
  // never imported again, and nothing is drawn twice.
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || !imported) return;
    const { objects: known, config: current } = dressing.current;
    const index = indexObjects(known);
    let registry;
    let root;
    try {
      registry = viewer.get("elementRegistry");
      root = viewer.get("canvas").getRootElement();
    } catch (err) {
      return;
    }
    registry.getAll().forEach((el) => {
      if (!el || el === root) return;
      const gfx = registry.getGraphics(el);
      if (!gfx) return;
      const family = el.labelTarget ? "label" : shapeFamily(el.type);
      if (family) gfx.classList.add(`bf-el--${family}`);
      const element = {
        guid: guidFromElementId(el.id),
        name: el.businessObject?.name || "",
        hasDiagram: drillable.current.has(el.id),
        width: el.width,
        height: el.height,
      };
      if (!el.labelTarget) describe(gfx, describeElement(element, accentFor(current, index, element)));
      if (el.labelTarget || family === "sequence" || family === "message" || family === "association") return;
      const accent = accentFor(current, index, element);
      if (accent) {
        gfx.style.setProperty("--bf-accent", accent.colour);
        gfx.style.setProperty("--bf-wash", accent.wash);
        gfx.style.setProperty("--bf-line", accent.line);
        gfx.classList.add("bf-el--typed");
        if (isBox(family)) paint(gfx, accentBar(element), "accent", { fill: accent.colour });
      }
      if (family === "participant" || family === "lane") {
        paint(gfx, headerBand(element), "band", {
          fill: accent ? accent.wash : "#eff2f7",
          before: true,
        });
      }
    });
  }, [imported, badges, objects, config]);

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || !xml) return undefined;
    let cancelled = false;
    drillable.current = new Set();
    callbacks.current.onDrillableCount?.(0);
    callbacks.current.onElements?.([]);
    // Did anything actually make it onto the canvas? bpmn-js reports a problem
    // for a file it has still drawn, and fitting the view or marking drillable
    // elements can fail on its own - none of that means the reader sees
    // nothing, so none of it may claim the diagram is unreadable.
    const drewSomething = () => {
      try {
        return viewer.get("elementRegistry").getAll().length > 0;
      } catch (err) {
        return false;
      }
    };

    viewer
      .importXML(xml)
      .then(({ warnings } = {}) => {
        if (cancelled || viewerRef.current !== viewer) return;
        if (warnings?.length) console.info("BPMN imported with warnings:", warnings);
        // What the file says about the drawing as a whole - its name, the
        // paragraph somebody wrote under it, the properties it carries - so
        // that clicking the empty canvas says something, and so that an
        // imported model's own description is readable somewhere.
        const whole = readProperties(viewer, viewer.get("canvas").getRootElement(), diagramProperties);
        callbacks.current.onDiagramProperties?.(whole?.rows || []);
        // And what it contains, for the panel that asks it questions.
        callbacks.current.onElements?.(readElements(viewer));
        try {
          // The whole diagram, on open, every time.
          fitSensibly(viewer);
        } catch (err) {
          // The diagram is on screen; this only costs the fit.
          console.error("BPMN rendered, but fitting the view failed:", err);
        }
        setImported((n) => n + 1);
      })
      .catch((err) => {
        console.error(err);
        if (cancelled || viewerRef.current !== viewer) return;
        if (drewSomething()) {
          console.info("BPMN reported a problem but was drawn; leaving it on screen.");
          setImported((n) => n + 1);
          return;
        }
        callbacks.current.onError?.("This BPMN file couldn't be displayed.");
      });
    return () => {
      cancelled = true;
    };
  }, [xml]);

  return (
    <div
      ref={containerRef}
      className="bf-bpmn-canvas"
      // Reachable by keyboard, and visibly so: with the canvas focused, the
      // arrow keys pan it.
      tabIndex={0}
      role="group"
      aria-label="Flow diagram"
      style={{ width: "100%", height: "100%" }}
    />
  );
});

export default BpmnCanvas;
